import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { v4 as uuidv4 } from 'uuid';

const DIFFICULTY_LABELS: Record<string, string> = {
  beginner: 'Débutant',
  intermediate: 'Intermédiaire',
  advanced: 'Avancé',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Brouillon',
  published: 'Publié',
  archived: 'Archivé',
};

const LANGUAGE_LABELS: Record<string, string> = {
  fr: 'Français',
  en: 'Anglais',
  es: 'Espagnol',
  de: 'Allemand',
};
import { supabase } from '../../lib/supabase';
import { 
  generateCourseSlug,
  type ElearningCategory,
  type ElearningCourse
} from '../../services/elearningCourseService';
import { useTenantContext } from '../../context/TenantContext';
import { useToast } from '../../hooks/useToast';
import { InstructorLayout } from '../../components/instructor/InstructorLayout';
import { useInstructorPermissions } from '../../hooks/useInstructorPermissions';
import { 
  ArrowLeft, 
  Save, 
  Eye, 
  Upload, 
  X,
  Plus,
  Loader2,
  BookOpen,
  Image,
  Video
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { MediaSelector } from '../../components/media/MediaSelector';

interface CourseFormData {
  title: string;
  slug: string;
  description: string;
  content: string;
  price: number;
  currency: string;
  thumbnail_url: string;
  video_preview_url: string;
  difficulty_level: 'beginner' | 'intermediate' | 'advanced';
  duration_hours: number;
  language: string;
  status: 'draft' | 'published' | 'archived';
  category_id: string;
  tags: string[];
  requirements: string[];
  learning_objectives: string[];
  is_featured: boolean;
}

const InstructorCourseFormPage: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ id?: string }>();
  const courseId = params.id;
  const { tenant } = useTenantContext();
  const { toast } = useToast();
  const { instructor, hasPermission, isLoading: authLoading } = useInstructorPermissions(['create_courses', 'edit_own_courses']);

  const isEditing = Boolean(courseId && courseId !== 'new');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  const [categories, setCategories] = useState<ElearningCategory[]>([]);
  
  const [formData, setFormData] = useState<CourseFormData>({
    title: '',
    slug: '',
    description: '',
    content: '',
    price: 0,
    currency: 'FCFA',
    thumbnail_url: '',
    video_preview_url: '',
    difficulty_level: 'beginner',
    duration_hours: 1,
    language: 'fr',
    status: 'draft',
    category_id: '',
    tags: [],
    requirements: [],
    learning_objectives: [],
    is_featured: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [newTag, setNewTag] = useState('');
  const [newRequirement, setNewRequirement] = useState('');
  const [newObjective, setNewObjective] = useState('');
  const [showThumbnailSelector, setShowThumbnailSelector] = useState(false);
  const [showVideoSelector, setShowVideoSelector] = useState(false);

  const DIFFICULTY_LABELS: Record<string, string> = {
    beginner: 'Débutant',
    intermediate: 'Intermédiaire',
    advanced: 'Avancé',
  };

  const STATUS_LABELS: Record<string, string> = {
    draft: 'Brouillon',
    published: 'Publié',
    archived: 'Archivé',
  };

  // Vérification des permissions
  useEffect(() => {
    if (authLoading) return;
    
    if (isEditing && !hasPermission('edit_own_courses')) {
      toast({
        title: "Accès refusé",
        description: "Vous n'avez pas les permissions pour modifier les cours.",
        variant: "destructive"
      });
      navigate('/instructor/courses');
      return;
    }
    
    if (!isEditing && !hasPermission('create_courses')) {
      toast({
        title: "Accès refusé", 
        description: "Vous n'avez pas les permissions pour créer des cours.",
        variant: "destructive"
      });
      navigate('/instructor/courses');
      return;
    }
  }, [authLoading, hasPermission, isEditing, navigate, toast]);

  const uploadFileAndGetUrl = async (file: File, folderName: string): Promise<string> => {
    const tenantId = tenant?.id || import.meta.env.VITE_TENANT_ID || '2';
    const fileExt = file.name.split('.').pop();
    const fileName = `${folderName}_${uuidv4()}.${fileExt}`;
    const filePath = `tenant/${tenantId}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('media')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (uploadError) {
      throw uploadError;
    }

    const { data: publicUrlData } = supabase.storage
      .from('media')
      .getPublicUrl(filePath);

    return publicUrlData.publicUrl;
  };

  useEffect(() => {
    console.log('useEffect triggered', { tenant, instructor, isEditing, courseId });
    if (!tenant || !instructor) return;

    loadInitialData();
  }, [tenant, instructor, isEditing, courseId]);

  const loadInitialData = async () => {
    try {
      console.log('Loading initial data...', { isEditing, courseId, instructorId: instructor?.id });
      setIsLoading(true);
      
      // Charger les catégories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('elearn_categories')
        .select('*')
        .eq('tenant_id', tenant!.id)
        .eq('is_active', true)
        .order('name');

      if (categoriesError) throw categoriesError;
      setCategories(categoriesData || []);

      // Si on édite un cours, charger ses données
      if (isEditing && courseId) {
        console.log('Fetching course data...', { courseId, tenantId: tenant!.id, instructorId: instructor!.id });
        
        // Vérifier d'abord si le cours existe sans filtrer par instructeur
        const { data: courseCheck, error: courseCheckError } = await supabase
          .from('elearn_courses')
          .select('id, title, instructor_id')
          .eq('id', courseId)
          .eq('tenant_id', tenant!.id)
          .single();
          
        console.log('Course check result:', { courseCheck, courseCheckError, expectedInstructorId: instructor!.id });
        
        // Si le cours existe mais n'appartient pas à l'instructeur, afficher un message spécifique
        if (courseCheck && courseCheck.instructor_id !== instructor!.id) {
          console.error('Course belongs to different instructor:', { 
            courseId, 
            courseInstructorId: courseCheck.instructor_id, 
            currentUserInstructorId: instructor!.id 
          });
          
          toast({
            title: "Accès refusé",
            description: "Vous ne pouvez pas éditer un cours qui ne vous appartient pas.",
            variant: "destructive"
          });
          navigate('/instructor/courses');
          return;
        }
        
        // Récupérer les données complètes du cours
        const { data: courseData, error: courseError } = await supabase
          .from('elearn_courses')
          .select('*')
          .eq('id', courseId)
          .eq('tenant_id', tenant!.id)
          .eq('instructor_id', instructor!.id) // S'assurer que l'enseignant peut seulement éditer ses propres cours
          .single();

        console.log('Course data result:', { courseData, courseError });
        
        if (courseError) {
          console.error('Course fetch error:', courseError);
          if (courseError.code === 'PGRST116') {
            toast({
              title: "Cours non trouvé",
              description: "Ce cours n'existe pas ou vous n'avez pas les permissions pour l'éditer.",
              variant: "destructive"
            });
            navigate('/instructor/courses');
            return;
          }
          throw courseError;
        }

        if (courseData) {
          console.log('Setting form data with course data:', courseData);
          setFormData({
            title: courseData.title || '',
            slug: courseData.slug || '',
            description: courseData.description || '',
            content: courseData.content || '',
            price: courseData.price || 0,
            currency: courseData.currency || 'FCFA',
            thumbnail_url: courseData.thumbnail_url || '',
            video_preview_url: courseData.video_preview_url || '',
            difficulty_level: courseData.difficulty_level || 'beginner',
            duration_hours: courseData.duration_hours || 1,
            language: courseData.language || 'fr',
            status: courseData.status || 'draft',
            category_id: courseData.category_id || '',
            tags: courseData.tags || [],
            requirements: courseData.requirements || [],
            learning_objectives: courseData.learning_objectives || [],
            is_featured: courseData.is_featured || false
          });
        } else {
          console.log('No course data found');
        }
      } else {
        console.log('Not in edit mode or no courseId');
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les données nécessaires.",
        variant: "destructive"
      });
    } finally {
      console.log('Finished loading initial data');
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof CourseFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Générer automatiquement le slug à partir du titre
    if (field === 'title' && value) {
      const slug = generateCourseSlug(value);
      setFormData(prev => ({
        ...prev,
        slug
      }));
    }

    // Effacer l'erreur pour ce champ
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est requis';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'La description est requise';
    }

    if (!formData.category_id) {
      newErrors.category_id = 'La catégorie est requise';
    }

    if (formData.price < 0) {
      newErrors.price = 'Le prix ne peut pas être négatif';
    }

    if (formData.duration_hours <= 0) {
      newErrors.duration_hours = 'La durée doit être supérieure à 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: "Erreurs de validation",
        description: "Veuillez corriger les erreurs dans le formulaire.",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsSaving(true);

      const courseData = {
        ...formData,
        tenant_id: tenant!.id,
        instructor_id: instructor!.id,
        updated_at: new Date().toISOString()
      };

      if (isEditing && courseId) {
        // Mise à jour du cours existant
        const { error } = await supabase
          .from('elearn_courses')
          .update(courseData)
          .eq('id', courseId)
          .eq('tenant_id', tenant!.id)
          .eq('instructor_id', instructor!.id);

        if (error) throw error;

        toast({
          title: "Cours mis à jour",
          description: "Le cours a été mis à jour avec succès.",
          variant: "default"
        });
      } else {
        // Création d'un nouveau cours
        const { data, error } = await supabase
          .from('elearn_courses')
          .insert([{
            ...courseData,
            id: uuidv4(),
            created_at: new Date().toISOString()
          }])
          .select()
          .single();

        if (error) throw error;

        toast({
          title: "Cours créé",
          description: "Le cours a été créé avec succès.",
          variant: "default"
        });

        // Rediriger vers l'édition du cours nouvellement créé
        navigate(`/instructor/courses/${data.id}/edit`);
        return;
      }

      // Rediriger vers la liste des cours
      navigate('/instructor/courses');

    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la sauvegarde du cours.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const addRequirement = () => {
    if (newRequirement.trim() && !formData.requirements.includes(newRequirement.trim())) {
      setFormData(prev => ({
        ...prev,
        requirements: [...prev.requirements, newRequirement.trim()]
      }));
      setNewRequirement('');
    }
  };

  const removeRequirement = (reqToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.filter(req => req !== reqToRemove)
    }));
  };

  const addObjective = () => {
    if (newObjective.trim() && !formData.learning_objectives.includes(newObjective.trim())) {
      setFormData(prev => ({
        ...prev,
        learning_objectives: [...prev.learning_objectives, newObjective.trim()]
      }));
      setNewObjective('');
    }
  };

  const removeObjective = (objToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      learning_objectives: prev.learning_objectives.filter(obj => obj !== objToRemove)
    }));
  };

  if (authLoading || isLoading) {
    return (
      <InstructorLayout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-white" />
        </div>
      </InstructorLayout>
    );
  }

  return (
    <InstructorLayout>
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto"
        >
          {/* En-tête */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/instructor/courses')}
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Retour aux cours
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-white">
                  {isEditing ? 'Modifier le cours' : 'Créer un nouveau cours'}
                </h1>
                <p className="text-gray-300 mt-1">
                  {isEditing ? 'Modifiez les informations de votre cours' : 'Créez un nouveau cours pour vos étudiants'}
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Colonne principale */}
              <div className="lg:col-span-2 space-y-6">
                {/* Informations de base */}
                <Card className="bg-black/20 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Informations de base</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="title" className="text-white">Titre du cours *</Label>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        className="bg-black/30 border-white/20 text-white"
                        placeholder="Ex: Introduction à React"
                      />
                      {errors.title && <p className="text-red-400 text-sm mt-1">{errors.title}</p>}
                    </div>

                    <div>
                      <Label htmlFor="slug" className="text-white">Slug (URL)</Label>
                      <Input
                        id="slug"
                        value={formData.slug}
                        onChange={(e) => handleInputChange('slug', e.target.value)}
                        className="bg-black/30 border-white/20 text-white"
                        placeholder="introduction-a-react"
                      />
                      <p className="text-gray-400 text-sm mt-1">
                        Généré automatiquement à partir du titre
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="description" className="text-white">Description *</Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        className="bg-black/30 border-white/20 text-white min-h-[100px]"
                        placeholder="Décrivez votre cours en quelques phrases..."
                      />
                      {errors.description && <p className="text-red-400 text-sm mt-1">{errors.description}</p>}
                    </div>

                    <div>
                      <Label htmlFor="content" className="text-white">Contenu détaillé</Label>
                      <Textarea
                        id="content"
                        value={formData.content}
                        onChange={(e) => handleInputChange('content', e.target.value)}
                        className="bg-black/30 border-white/20 text-white min-h-[200px]"
                        placeholder="Décrivez le contenu détaillé de votre cours..."
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Médias */}
                <Card className="bg-black/20 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Médias</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-white">Image de couverture</Label>
                      <Input
                        type="text"
                        value={formData.thumbnail_url}
                        onChange={(e) => handleInputChange('thumbnail_url', e.target.value)}
                        placeholder="URL de l'image de couverture"
                        className="bg-black/30 border-white/20 text-white mt-2"
                      />
                      {formData.thumbnail_url && (
                        <div className="relative w-full h-48 rounded-md overflow-hidden mt-3">
                          <img
                            src={formData.thumbnail_url}
                            alt="Aperçu de l'image de couverture"
                            className="w-full h-full object-cover"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleInputChange('thumbnail_url', '')}
                            className="absolute top-2 right-2 bg-black/50 text-white hover:bg-black/70"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                      <div className="flex gap-2 mt-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowThumbnailSelector(true)}
                          className="border-white/20 text-white hover:bg-white/10"
                        >
                          <Image className="h-4 w-4 mr-2" />
                          Médiathèque
                        </Button>
                        <Button
                          type="button"
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*';
                            input.onchange = async (e: any) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setIsSaving(true);
                                try {
                                  const url = await uploadFileAndGetUrl(file, 'course_thumbnails');
                                  handleInputChange('thumbnail_url', url);
                                  toast({ title: 'Image uploadée', description: "L'image a été uploadée avec succès." });
                                } catch (err) {
                                  console.error('Upload image error:', err);
                                  toast({ title: "Erreur d'upload", description: "Impossible d'uploader l'image.", variant: 'destructive' });
                                } finally {
                                  setIsSaving(false);
                                }
                              }
                            };
                            input.click();
                          }}
                          className="bg-gray-800 text-white hover:bg-gray-700"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Uploader
                        </Button>
                      </div>
                    </div>

                    <div>
                      <Label className="text-white">Vidéo de présentation</Label>
                      <Input
                        type="text"
                        value={formData.video_preview_url}
                        onChange={(e) => handleInputChange('video_preview_url', e.target.value)}
                        placeholder="URL de la vidéo de présentation"
                        className="bg-black/30 border-white/20 text-white mt-2"
                      />
                      {formData.video_preview_url && (
                        <div className="relative w-full rounded-md overflow-hidden mt-3">
                          <video
                            src={formData.video_preview_url}
                            className="w-full h-48 object-cover"
                            controls
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleInputChange('video_preview_url', '')}
                            className="absolute top-2 right-2 bg-black/50 text-white hover:bg-black/70"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                      <div className="flex gap-2 mt-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowVideoSelector(true)}
                          className="border-white/20 text-white hover:bg-white/10"
                        >
                          <Video className="h-4 w-4 mr-2" />
                          Médiathèque
                        </Button>
                        <Button
                          type="button"
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'video/*';
                            input.onchange = async (e: any) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setIsSaving(true);
                                try {
                                  const url = await uploadFileAndGetUrl(file, 'course_videos');
                                  handleInputChange('video_preview_url', url);
                                  toast({ title: 'Vidéo uploadée', description: 'La vidéo a été uploadée avec succès.' });
                                } catch (err) {
                                  console.error('Upload video error:', err);
                                  toast({ title: "Erreur d'upload", description: 'Impossible d\'uploader la vidéo.', variant: 'destructive' });
                                } finally {
                                  setIsSaving(false);
                                }
                              }
                            };
                            input.click();
                          }}
                          className="bg-gray-800 text-white hover:bg-gray-700"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Uploader
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Tags */}
                <Card className="bg-black/20 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Tags</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex space-x-2">
                      <Input
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                        placeholder="Ajouter un tag..."
                        className="bg-black/30 border-white/20 text-white"
                      />
                      <Button type="button" onClick={addTag} variant="outline" className="border-white/20 text-white hover:bg-white/10">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.tags.map((tag, index) => (
                        <Badge key={index} variant="secondary" className="bg-blue-600/20 text-blue-300 border-blue-500/30">
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="ml-2 text-blue-300 hover:text-white"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Prérequis */}
                <Card className="bg-black/20 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Prérequis</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex space-x-2">
                      <Input
                        value={newRequirement}
                        onChange={(e) => setNewRequirement(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addRequirement())}
                        placeholder="Ajouter un prérequis..."
                        className="bg-black/30 border-white/20 text-white"
                      />
                      <Button type="button" onClick={addRequirement} variant="outline" className="border-white/20 text-white hover:bg-white/10">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {formData.requirements.map((req, index) => (
                        <div key={index} className="flex items-center justify-between bg-black/30 p-3 rounded-lg">
                          <span className="text-white">{req}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeRequirement(req)}
                            className="text-red-400 hover:bg-red-900/20"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Objectifs d'apprentissage */}
                <Card className="bg-black/20 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Objectifs d'apprentissage</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex space-x-2">
                      <Input
                        value={newObjective}
                        onChange={(e) => setNewObjective(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addObjective())}
                        placeholder="Ajouter un objectif..."
                        className="bg-black/30 border-white/20 text-white"
                      />
                      <Button type="button" onClick={addObjective} variant="outline" className="border-white/20 text-white hover:bg-white/10">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {formData.learning_objectives.map((obj, index) => (
                        <div key={index} className="flex items-center justify-between bg-black/30 p-3 rounded-lg">
                          <span className="text-white">{obj}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeObjective(obj)}
                            className="text-red-400 hover:bg-red-900/20"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Colonne latérale - Paramètres */}
              <div className="space-y-6">
                <Card className="bg-black/20 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Paramètres</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                      <div>
                        <Label htmlFor="category_id" className="text-white">Catégorie *</Label>
                        <Select
                          value={formData.category_id}
                          onValueChange={(value) => handleInputChange('category_id', value)}
                        >
                          <SelectTrigger className="w-full bg-gray-800 border-gray-700 text-white">
                            <SelectValue placeholder="Sélectionner une catégorie">
                              {formData.category_id ? categories.find(c => c.id === formData.category_id)?.name : 'Sélectionner une catégorie'}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 text-white border-gray-700">
                            {categories.map((cat) => (
                              <SelectItem key={cat.id} value={cat.id} className="hover:bg-gray-700">
                                {cat.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.category_id && <p className="text-red-400 text-sm mt-1">{errors.category_id}</p>}
                      </div>

                      <div>
                        <Label htmlFor="difficulty_level" className="text-white">Niveau de difficulté</Label>
                        <Select
                          value={formData.difficulty_level}
                          onValueChange={(value) => handleInputChange('difficulty_level', value)}
                        >
                          <SelectTrigger className="w-full bg-gray-800 border-gray-700 text-white">
                            <SelectValue placeholder="Sélectionner un niveau">
                              {formData.difficulty_level ? DIFFICULTY_LABELS[formData.difficulty_level] : 'Sélectionner un niveau'}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 text-white border-gray-700">
                            {Object.entries(DIFFICULTY_LABELS).map(([value, label]) => (
                              <SelectItem key={value} value={value}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="status" className="text-white">Statut</Label>
                        <Select
                          value={formData.status}
                          onValueChange={(value) => handleInputChange('status', value)}
                        >
                          <SelectTrigger className="w-full bg-gray-800 border-gray-700 text-white">
                            <SelectValue placeholder="Sélectionner un statut">
                              {formData.status ? STATUS_LABELS[formData.status] : 'Sélectionner un statut'}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 text-white border-gray-700">
                            {Object.entries(STATUS_LABELS).map(([value, label]) => (
                              <SelectItem key={value} value={value}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="price" className="text-white">Prix (FCFA)</Label>
                        <Input
                          id="price"
                          type="number"
                          min="0"
                          value={formData.price}
                          onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                          className="bg-black/30 border-white/20 text-white"
                        />
                        {errors.price && <p className="text-red-400 text-sm mt-1">{errors.price}</p>}
                      </div>
  
                      <div>
                        <Label htmlFor="duration_hours" className="text-white">Durée (heures)</Label>
                        <Input
                          id="duration_hours"
                          type="number"
                          min="0.5"
                          step="0.5"
                          value={formData.duration_hours}
                          onChange={(e) => handleInputChange('duration_hours', parseFloat(e.target.value) || 1)}
                          className="bg-black/30 border-white/20 text-white"
                        />
                        {errors.duration_hours && <p className="text-red-400 text-sm mt-1">{errors.duration_hours}</p>}
                      </div>

                      <div>
                        <Label htmlFor="language" className="text-white">Langue</Label>
                        <Select
                          value={formData.language}
                          onValueChange={(value) => handleInputChange('language', value)}
                        >
                          <SelectTrigger className="w-full bg-gray-800 border-gray-700 text-white">
                            <SelectValue placeholder="Sélectionner une langue">
                              {formData.language ? LANGUAGE_LABELS[formData.language] : 'Sélectionner une langue'}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 text-white border-gray-700">
                            {Object.entries(LANGUAGE_LABELS).map(([value, label]) => (
                              <SelectItem key={value} value={value}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Actions */}
                  <Card className="bg-black/20 border-white/10">
                    <CardContent className="p-4 space-y-3">
                      <Button
                        type="submit"
                        disabled={isSaving}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        {isSaving ? (
                          <>
                            <Loader2 className="animate-spin mr-2" size={16} />
                            Sauvegarde...
                          </>
                        ) : (
                          <>
                            <Save className="mr-2 text-white" size={16} />
                            {isEditing ? 'Mettre à jour' : 'Créer le cours'}
                          </>
                        )}
                      </Button>
                      
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate('/instructor/courses')}
                        className="w-full border-white/20 text-white hover:bg-white/10"
                      >
                        Annuler
                      </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </form>
          
          {/* Sélecteurs de média */}
          <MediaSelector
            isOpen={showThumbnailSelector}
            onClose={() => setShowThumbnailSelector(false)}
            onSelect={(url) => {
              handleInputChange('thumbnail_url', url);
              setShowThumbnailSelector(false);
              // Réouvrir le formulaire si nécessaire (à vérifier si isDialogOpen est utilisé ici)
              // setIsDialogOpen(true);
            }}
            selectedUrl={formData.thumbnail_url || undefined}
            title="Sélectionner une image de couverture"
            acceptedTypes={['image/*']}
          />
          
          <MediaSelector
            isOpen={showVideoSelector}
            onClose={() => setShowVideoSelector(false)}
            onSelect={(url) => {
              handleInputChange('video_preview_url', url);
              setShowVideoSelector(false);
              // Réouvrir le formulaire si nécessaire (à vérifier si isDialogOpen est utilisé ici)
              // setIsDialogOpen(true);
            }}
            selectedUrl={formData.video_preview_url || undefined}
            title="Sélectionner une vidéo de présentation"
            acceptedTypes={['video/*']}
          />
        </motion.div>
      </div>
    </InstructorLayout>
  );
};

export default InstructorCourseFormPage;
